import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { trainingApi } from "../../services/trainingApi";
import { usePermissions } from "../../hooks/usePermissions";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

const TABS = ["definitions", "issued", "waivers"];

export default function TrainingCertificationsPage() {
  const { t } = useLocalization();
  const { canViewTraining, canManageTrainingContent, canWaiveTraining } = usePermissions();
  const [tab, setTab] = useState("definitions");
  const [certDefs, setCertDefs] = useState([]);
  const [employeeCerts, setEmployeeCerts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [waivers, setWaivers] = useState([]);
  const [defForm, setDefForm] = useState({ key: "", title_en: "", title_ja: "", high_risk: false, renewal_period_days: "" });
  const [waiverForm, setWaiverForm] = useState({ employee_id: "", certification_key: "", reason: "", expires_at: "" });

  const TAB_LABELS = {
    definitions: t("mgmtTrainingCert.tabDefinitions"),
    issued: t("mgmtTrainingCert.tabIssued"),
    waivers: t("mgmtTrainingCert.tabWaivers"),
  };

  const load = () => {
    trainingApi.listCertificationDefs().then((r) => setCertDefs(r.data)).catch(() => {});
    trainingApi.listEmployeeCertifications().then((r) => setEmployeeCerts(r.data)).catch(() => {});
    trainingApi.listEmployeesForAssignment().then((r) => setEmployees(r.data)).catch(() => {});
    trainingApi.listWaivers().then((r) => setWaivers(r.data)).catch(() => {});
  };

  useEffect(() => { if (canViewTraining) load(); }, [canViewTraining]);

  if (!canViewTraining) return <p className="text-sm text-gray-500" data-testid="certifications-access-denied">{t("common.accessDenied")}</p>;

  const employeeName = (id) => employees.find((e) => e.id === id)?.legal_name || id;
  const today = new Date().toISOString().slice(0, 10);

  const createDef = async () => {
    if (!defForm.key || !defForm.title_en) { toast.error(t("mgmtTrainingCert.keyTitleRequired")); return; }
    try {
      await trainingApi.createCertificationDef({
        key: defForm.key, title_en: defForm.title_en, title_ja: defForm.title_ja || defForm.title_en,
        high_risk: defForm.high_risk, renewal_period_days: defForm.renewal_period_days ? Number(defForm.renewal_period_days) : undefined,
      });
      toast.success(t("mgmtTrainingCert.definitionCreated"));
      setDefForm({ key: "", title_en: "", title_ja: "", high_risk: false, renewal_period_days: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createWaiver = async () => {
    if (!waiverForm.employee_id || !waiverForm.reason || !waiverForm.expires_at) { toast.error(t("mgmtTrainingCert.employeeReasonExpiryRequired")); return; }
    try {
      await trainingApi.approveWaiver({ ...waiverForm, certification_key: waiverForm.certification_key || undefined });
      toast.success(t("mgmtTrainingCert.waiverApproved"));
      setWaiverForm({ employee_id: "", certification_key: "", reason: "", expires_at: "" });
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="certifications-page">
      <PageHelpBanner pageKey="training-certifications" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtTrainingCert.help") }} />
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtTraining.certifications")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtTrainingCert.subtitle")}</p>
      </div>

      <div className="flex gap-2 border-b border-gray-200">
        {TABS.map((tid) => (
          <button key={tid} data-testid={`cert-tab-${tid}`} onClick={() => setTab(tid)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === tid ? "border-blue-600 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-800"}`}>
            {TAB_LABELS[tid]}
          </button>
        ))}
      </div>

      {tab === "definitions" && (
        <div className="space-y-4">
          {canManageTrainingContent && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="create-cert-def-form">
              <input data-testid="cert-key-input" value={defForm.key} onChange={(e) => setDefForm({ ...defForm, key: e.target.value.toUpperCase().replace(/\s/g, "_") })} placeholder={t("mgmtTrainingCert.certKeyPlaceholder")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
              <input data-testid="cert-title-input" value={defForm.title_en} onChange={(e) => setDefForm({ ...defForm, title_en: e.target.value })} placeholder={t("mgmtTraining.titleEnPlaceholder")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
              <input data-testid="cert-renewal-input" type="number" value={defForm.renewal_period_days} onChange={(e) => setDefForm({ ...defForm, renewal_period_days: e.target.value })} placeholder={t("mgmtTrainingCert.renewalDaysPlaceholder")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
              <label className="text-xs text-gray-600 flex items-center gap-1"><input data-testid="cert-high-risk-checkbox" type="checkbox" checked={defForm.high_risk} onChange={(e) => setDefForm({ ...defForm, high_risk: e.target.checked })} /> {t("mgmtTrainingCert.highRisk")}</label>
              <button data-testid="create-cert-def-btn" onClick={createDef} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700">{t("mgmtTrainingCert.create")}</button>
            </div>
          )}
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("mgmtLeavePolicies.colKey")}</th><th className="px-4 py-3 font-medium">{t("mgmtLeavePolicies.colTitle")}</th><th className="px-4 py-3 font-medium">{t("mgmtTrainingCert.highRisk")}</th><th className="px-4 py-3 font-medium">{t("mgmtTrainingCert.renewal")}</th></tr></thead>
              <tbody>
                {certDefs.map((c) => (
                  <tr key={c.id} className="border-t border-gray-100" data-testid={`cert-def-row-${c.id}`}>
                    <td className="px-4 py-2 font-semibold text-gray-800">{c.key}</td>
                    <td className="px-4 py-2 text-gray-600">{c.title_en}</td>
                    <td className="px-4 py-2 text-gray-600">{c.high_risk ? t("mgmtTrainingCert.yes") : t("mgmtTrainingCert.no")}</td>
                    <td className="px-4 py-2 text-gray-600">{c.renewal_period_days ? `${c.renewal_period_days} ${t("common.days")}` : t("mgmtTrainingCert.none")}</td>
                  </tr>
                ))}
                {certDefs.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500" data-testid="no-cert-defs-row">{t("mgmtTrainingCert.noDefsYet")}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "issued" && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colEmployee")}</th><th className="px-4 py-3 font-medium">{t("mgmtTraining.certifications")}</th><th className="px-4 py-3 font-medium">{t("mgmtTrainingCert.issued")}</th><th className="px-4 py-3 font-medium">{t("mgmtLeaveDetail.expires")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th></tr></thead>
            <tbody>
              {employeeCerts.map((c) => (
                <tr key={c.id} className="border-t border-gray-100" data-testid={`employee-cert-row-${c.id}`}>
                  <td className="px-4 py-2 font-semibold text-gray-800">{employeeName(c.employee_id)}</td>
                  <td className="px-4 py-2 text-gray-600">{c.certification_key}</td>
                  <td className="px-4 py-2 text-gray-600">{(c.issued_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-2 text-gray-600">{c.expires_at ? c.expires_at.slice(0, 10) : t("mgmtLeaveDetail.never")}</td>
                  <td className="px-4 py-2">
                    <span className={`text-xs font-semibold ${c.expires_at && c.expires_at.slice(0, 10) <= today ? "text-red-600" : "text-emerald-600"}`}>
                      {c.expires_at && c.expires_at.slice(0, 10) <= today ? t("mgmtLeaveDetail.expired") : c.status}
                    </span>
                  </td>
                </tr>
              ))}
              {employeeCerts.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-gray-500" data-testid="no-employee-certs-row">{t("mgmtTrainingCert.noCertsIssued")}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "waivers" && (
        <div className="space-y-4">
          {canWaiveTraining && (
            <div className="bg-white border border-gray-200 rounded-xl p-4 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="create-waiver-form">
              <select data-testid="waiver-employee-select" value={waiverForm.employee_id} onChange={(e) => setWaiverForm({ ...waiverForm, employee_id: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm">
                <option value="">{t("mgmtTrainingAssignments.employeeEllipsis")}</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.legal_name}</option>)}
              </select>
              <input data-testid="waiver-cert-key-input" value={waiverForm.certification_key} onChange={(e) => setWaiverForm({ ...waiverForm, certification_key: e.target.value.toUpperCase() })} placeholder={t("mgmtTrainingCert.certKeyOptionalPlaceholder")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
              <input data-testid="waiver-reason-input" value={waiverForm.reason} onChange={(e) => setWaiverForm({ ...waiverForm, reason: e.target.value })} placeholder={t("mgmtPayrollLine.reasonPlaceholder")} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
              <input data-testid="waiver-expires-input" type="date" value={waiverForm.expires_at} onChange={(e) => setWaiverForm({ ...waiverForm, expires_at: e.target.value })} className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm" />
              <button data-testid="create-waiver-btn" onClick={createWaiver} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700">{t("mgmtTrainingCert.approveWaiver")}</button>
            </div>
          )}
          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-gray-50 text-gray-500 text-left"><tr><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colEmployee")}</th><th className="px-4 py-3 font-medium">{t("mgmtTraining.certifications")}</th><th className="px-4 py-3 font-medium">{t("mgmtPayrollLine.reasonPlaceholder")}</th><th className="px-4 py-3 font-medium">{t("mgmtLeaveDetail.expires")}</th></tr></thead>
              <tbody>
                {waivers.map((w) => (
                  <tr key={w.id} className="border-t border-gray-100" data-testid={`waiver-row-${w.id}`}>
                    <td className="px-4 py-2 font-semibold text-gray-800">{employeeName(w.employee_id)}</td>
                    <td className="px-4 py-2 text-gray-600">{w.certification_key || w.course_id || "-"}</td>
                    <td className="px-4 py-2 text-gray-600">{w.reason}</td>
                    <td className="px-4 py-2 text-gray-600">{(w.expires_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
                {waivers.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-500" data-testid="no-waivers-row">{t("mgmtTrainingCert.noWaiversIssued")}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
